/**
 * Centralized query keys for TanStack Query
 * Factory pattern: createQueryKeyFactory(base) -> { all, list, detail, ... }
 * Keep keys stable so cross-module invalidation works
 */

export const createQueryKeyFactory = (baseKey) => {
    const base = Array.isArray(baseKey) ? baseKey : [baseKey];
    return {
        all: base,
        lists: () => [...base, 'list'],
        list: (params = {}) => [...base, 'list', params],
        details: () => [...base, 'detail'],
        detail: (id) => [...base, 'detail', String(id)],
        infinite: (params = {}) => [...base, 'infinite', params],
        search: (query, params = {}) => [...base, 'search', query, params],
    };
};

export const queryKeys = {
    ekyc: {
        status: ['ekyc', 'status'],
        history: ['ekyc', 'history'],
        pendingReview: ['ekyc', 'operator', 'pending-review'],
        rekycConfig: ['ekyc', 'operator', 'rekyc-config'],
        factory: createQueryKeyFactory('ekyc'),
    },
    certificates: (options) => (options ? ['certificates', options] : ['certificates']),
    certificatesFactory: createQueryKeyFactory('certificates'),
    certificateInstallInfo: ['certificates', 'install-info', 'me'],
    certificateInstallInfoByUser: (userId) => ['certificates', 'install-info', String(userId)],
    billing: ['billing'],
    billingFactory: createQueryKeyFactory('billing'),
    payments: (params = {}) => ['payments', 'user', params],
    paymentsFactory: createQueryKeyFactory(['payments', 'user']),
    paymentRequirement: ['payments', 'requirement'],
    adminPayments: (params = {}) => ['payments', 'admin', params],
    adminPaymentsFactory: createQueryKeyFactory(['payments', 'admin']),
    adminOfflinePayments: (params = {}) => ['payments', 'admin', 'offline', params],
    paymentReconciliationQueue: ['payments', 'admin', 'reconciliation'],
    paymentFailed: ['payments', 'admin', 'failed'],
    paymentAudit: (paymentId) => ['payments', 'admin', paymentId, 'audit'],
    paymentLink: {
        status: (token) => ['payment-link', 'status', token],
        factory: createQueryKeyFactory('payment-link'),
    },
    featureFlags: ['feature-flags'],
    featureFlagsPublic: ['feature-flags', 'public'],
    products: (params = {}) => ['products', params],
    productsFactory: createQueryKeyFactory('products'),
    adminProducts: (params = {}) => ['products', 'admin', params],
    adminSettings: ['settings', 'admin'],
    adminActivityLogs: (params = {}) => ['activity-logs', 'admin', params],
    userActivityLogs: (params = {}) => ['activity-logs', 'user', params],
    operatorActivityLogs: (params = {}) => ['activity-logs', 'operator', params],
    operators: (params = {}) => ['operators', params],
    operatorsFactory: createQueryKeyFactory('operators'),
    certificateProfiles: (params) => (params ? ['certificate-profiles', params] : ['certificate-profiles']),
    certificateProfileExtraCosts: (profileId) => ['certificate-profiles', 'extra-costs', profileId],
    users: (params = {}) => ['users', params],
    usersFactory: createQueryKeyFactory('users'),
    evidence: {
        myEvidence: (params = {}) => ['evidence', 'mine', params],
        detail: (evidenceId) => ['evidence', 'detail', evidenceId],
        reviewQueue: (params = {}) => ['evidence', 'review', params],
        factory: createQueryKeyFactory('evidence'),
    },
    signingHistory: (params = {}) => ['pdf', 'history', params],
    notifications: {
        list: (scope = 'user', params = {}) => ['notifications', scope, 'list', params],
        unread: (scope = 'user') => ['notifications', scope, 'unread'],
        factory: (scope = 'user') => createQueryKeyFactory(['notifications', scope]),
    },
    tickets: (scope, params = {}) => ['tickets', scope, params],
    ticketsFactory: (scope) => createQueryKeyFactory(['tickets', scope]),
    ticketDetail: (ticketId) => ['tickets', 'detail', ticketId],
    ticketDetailFactory: createQueryKeyFactory(['tickets', 'detail']),
    maintenance: {
        status: ['maintenance', 'status'],
        factory: createQueryKeyFactory('maintenance'),
    },
};

export default queryKeys;
